import * as React from "react";
import { useClinicState } from "../../clinic/clinicStateContext";
import { ClinicConfigKeys } from "fe-common/graphql/clinic/clinicConfigKeys";
import { ClinicV1Settings } from "./ClinicV1Settings";
import { useConfigurationSettingsManager } from "fe-web-components/settings/common/useConfigurationSettingsManager";
import { ConfigurationSettingList } from "@kahunmedical/common-configuration/dist/lib/settings";
import { ConfigurationDottedKey } from "@kahunmedical/common-configuration/dist/lib/annotations";
import { KahunConfigurationManagementPage } from "fe-web-components/settings/newSettings/kahunConfigurationManagementPage";
import { useMemo } from "react";
import { clearErrorlyStringifiedValues } from "fe-web-components/settings/common/getV2ClinicSettings";
import { useUpdateClinicConfig } from "./hooks/useUpdateClinicConfig";
import { ClinicPageWrapper } from "./elements/clinicPageWrapper";
import { LoadingProgress } from "fe-web-components/loader/loadingProgress";

//region [[ Styles ]]

//endregion [[ Styles ]]

//region [[ Props ]]
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ClinicSettingManagement = () => {
  const {
    config,
    createStoreKeyHandler,
    isReady,
    mergedConfiguration,
  } = useUpdateClinicConfig();

  const [{ isMasterClinic }] = useClinicState();

  const flatSettings = useMemo(
    () =>
      !isReady
        ? {}
        : (config[
            ClinicConfigKeys.UnifiedConfigString
          ] as ConfigurationSettingList) || {},
    [config, isReady],
  );

  const anyOldSettings = Object.keys(config).length;

  const settingsManager = useConfigurationSettingsManager(mergedConfiguration);

  const saveToClinicSettings = (configToSave: ConfigurationSettingList) => {
    // Merge local changes with local storage, clear stingified migration fails
    const newObjValue = clearErrorlyStringifiedValues({
      ...(flatSettings || {}),
      ...configToSave,
    });

    return createStoreKeyHandler(ClinicConfigKeys.UnifiedConfigString)(
      newObjValue,
    );
  };

  const getAllNonDefault = () => {
    // Get current changes
    const currentChanges = settingsManager.settings.getNonDefaultSettings();
    // Return a merged object based on the current saved data
    return {
      ...(flatSettings || {}),
      ...currentChanges,
    } as ConfigurationSettingList;
  };

  // Delete local storage and re-initilize by reading localStorage data
  const handleResetDefault = (root: ConfigurationDottedKey) => {
    // Clear current local storage data from current root
    const newUnifiedString = Object.entries(flatSettings || {}).reduce(
      (acc, [k, v]) => (k.startsWith(root) ? acc : { ...acc, [k]: v }),
      {},
    );

    return createStoreKeyHandler(ClinicConfigKeys.UnifiedConfigString)(
      newUnifiedString,
    );
  };

  if (!isReady)
    return (
      <LoadingProgress
        label="Loading configuration..."
        style={{ margin: "20px 0" }}
      />
    );

  /******************************************************************/
  /*           Check if we need to support old settings             */
  /******************************************************************/
  if ((!flatSettings || !Object.keys(flatSettings).length) && anyOldSettings)
    return <ClinicV1Settings />;
  /***************************************************************/

  return (
    <ClinicPageWrapper className={"clinic-settings-management"}>
      <KahunConfigurationManagementPage
        configSaver={saveToClinicSettings}
        settingsManager={settingsManager}
        resetDefaults={handleResetDefault}
        getAllNonDefaultSettings={getAllNonDefault}
        showBackofficeSettings
        isMasterClinic={isMasterClinic}
      />
    </ClinicPageWrapper>
  );
};
